import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Users, Search, CheckCircle2, Download, Filter, Clock, Trophy } from "lucide-react";
import { buildQuestionsOrAnswers, buildCombined, downloadImages } from "@/lib/reportImages";

function fmtDuration(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function PlaytimeReportCard() {
  const [pt, setPt] = useState(null);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => { api.get("/admin/playtime-report").then((r) => setPt(r.data)).catch(() => {}); }, []);
  if (!pt) return null;
  const rows = showAll ? pt.per_user : pt.per_user.slice(0, 10);
  return (
    <div className="brutal-card p-5" data-testid="playtime-report">
      <h3 className="font-black uppercase mb-1 flex items-center gap-2"><Clock size={18} /> Play Time Played</h3>
      <p className="text-sm text-neutral-600 mb-4">Total time employees spent playing games. Daily limit: <strong>{pt.limit_minutes === 0 ? "Unlimited" : `${pt.limit_minutes} min`}</strong> per employee (resets midnight IST).</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <div className="border-2 border-black p-3 bg-[#39FF14]">
          <div className="text-xs uppercase font-mono text-black/70">Total Hours (all-time)</div>
          <div className="text-3xl font-black" data-testid="pt-total-all">{fmtDuration(pt.total_seconds_all)}</div>
        </div>
        <div className="border-2 border-black p-3 bg-[#FFD600]">
          <div className="text-xs uppercase font-mono text-black/70">Played Today</div>
          <div className="text-3xl font-black" data-testid="pt-total-today">{fmtDuration(pt.total_seconds_today)}</div>
        </div>
        <div className="border-2 border-black p-3 bg-white">
          <div className="text-xs uppercase font-mono text-neutral-500">Active Today</div>
          <div className="text-3xl font-black">{pt.active_today}</div>
        </div>
      </div>
      {pt.per_user.length === 0 ? (
        <p className="text-sm text-neutral-500">No play-time recorded yet.</p>
      ) : (
        <div className="border-2 border-black overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black text-white">
              <tr>{["Employee", "Dept", "Today", "All-time"].map((h) => <th key={h} className="text-left px-3 py-2 font-black uppercase text-xs">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.user_id} data-testid={`pt-row-${u.employee_id}`} className="border-t-2 border-black">
                  <td className="px-3 py-2 font-bold">{u.name} <span className="text-neutral-400 font-normal">({u.employee_id})</span></td>
                  <td className="px-3 py-2">{u.department || "—"}</td>
                  <td className="px-3 py-2 font-black">{fmtDuration(u.today_seconds)}</td>
                  <td className="px-3 py-2 font-black">{fmtDuration(u.total_seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {pt.per_user.length > 10 && (
        <button data-testid="pt-toggle-all" onClick={() => setShowAll((v) => !v)} className="text-xs font-black uppercase mt-2 underline">{showAll ? "Show top 10" : `Show all ${pt.per_user.length}`}</button>
      )}
    </div>
  );
}

function TopWinnersCard() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const GAME_ORDER = [["rebus", "Rebus"], ["word", "Word Building"], ["sudoku", "Sudoku"]];
  const isRange = startDate !== endDate;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/top-winners?start=${startDate}&end=${endDate}&limit=6`);
      setData(r.data);
    } catch { toast.error("Failed to load top winners"); } finally { setLoading(false); }
  }, [startDate, endDate]);
  useEffect(() => { load(); }, [load]);

  const [combinedLoading, setCombinedLoading] = useState(false);
  const buildCombinedImg = async () => {
    setCombinedLoading(true);
    try {
      const [wr, ar] = await Promise.all([
        api.get(`/admin/top-winners?date=${date}&limit=6`),
        api.get(`/admin/answers?date=${date}`),
      ]);
      const imgs = await buildCombined({ ...wr.data, date }, ar.data);
      if (!imgs.length) return toast.error("Nothing to show for this day");
      downloadImages(imgs);
      toast.success("Combined report image downloaded");
    } catch { toast.error("Failed to build combined image"); }
    finally { setCombinedLoading(false); }
  };

  const [qLoading, setQLoading] = useState(false);
  const buildDay = async (mode, setBusy) => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/answers?date=${date}`);
      const imgs = await buildQuestionsOrAnswers(mode, r.data);
      if (!imgs.length) return toast.error("Nothing scheduled for this day");
      downloadImages(imgs);
      const noun = mode === "questions" ? "Questions" : "Answers";
      toast.success(imgs.length > 1 ? `${noun} images downloaded (${imgs.length} pages)` : `${noun} image downloaded`);
    } catch { toast.error(`Failed to build ${mode} image`); }
    finally { setBusy(false); }
  };

  return (
    <div className="brutal-card p-5" data-testid="top-winners">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h3 className="font-black uppercase flex items-center gap-2"><Trophy size={18} /> Top Performers</h3>
          <p className="text-sm text-neutral-600">Daily leaders per game. Pick a single day, or a <strong>date range</strong> to rank top performers over that period (IST).</p>
          <p className="text-xs text-neutral-500 mt-1">Report images are 1920×1080. More than 3 games in a day split into separate page files.</p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4 border-2 border-black p-3 bg-neutral-50">
        <div>
          <label className="text-[11px] font-black uppercase block">From</label>
          <input data-testid="tw-start-date" type="date" value={startDate} max={endDate} onChange={(e) => setStartDate(e.target.value)} className="brutal-input" />
        </div>
        <div>
          <label className="text-[11px] font-black uppercase block">To</label>
          <input data-testid="tw-end-date" type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} className="brutal-input" />
        </div>
        <button data-testid="tw-download-combined" onClick={buildCombinedImg} disabled={combinedLoading} className="brutal-btn flex items-center gap-2 text-sm disabled:opacity-50"><Download size={14} /> {combinedLoading ? "Building…" : "Combined Image (Winners + Answers)"}</button>
        <div className="h-8 w-px bg-black/20 mx-1 hidden sm:block" />
        <div>
          <label className="text-[11px] font-black uppercase block">Single day (combined report)</label>
          <input data-testid="tw-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="brutal-input" />
        </div>
        <button data-testid="tw-download-questions" onClick={() => buildDay("questions", setQLoading)} disabled={qLoading} className="brutal-btn-secondary flex items-center gap-2 text-sm disabled:opacity-50"><Download size={14} /> {qLoading ? "Building…" : "Questions Image"}</button>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500 py-4 text-center">Loading…</div>
      ) : (
        <div className="grid md:grid-cols-3 gap-4">
          {GAME_ORDER.map(([key, label]) => {
            const rows = (data?.games?.[key]) || [];
            return (
              <div key={key} className="border-2 border-black" data-testid={`tw-game-${key}`}>
                <div className="bg-black text-white px-3 py-2 font-black uppercase text-xs">{label}{isRange ? " · Range" : ""}</div>
                {rows.length === 0 ? (
                  <div className="p-4 text-center text-xs text-neutral-400">No winners yet</div>
                ) : (
                  <table className="w-full text-sm">
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.employee_id + r.rank} data-testid={`tw-${key}-row-${r.rank}`} className="border-t-2 border-black">
                          <td className="px-2 py-1.5 font-black w-8 text-center" style={{ background: r.rank === 1 ? "#FFD600" : r.rank === 2 ? "#e5e5e5" : r.rank === 3 ? "#f5c98a" : "transparent" }}>{r.rank}</td>
                          <td className="px-2 py-1.5"><div className="font-bold leading-tight">{r.name}</div><div className="text-[10px] text-neutral-400">{r.employee_id}{r.department ? ` · ${r.department}` : ""}</div></td>
                          <td className="px-2 py-1.5 text-right font-black whitespace-nowrap">{r.points}<span className="text-[10px] text-neutral-400 block">{(data?.units?.[key]) || "pts"}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ReportsPanel() {
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const GAMES = [
    { key: "all",        label: "All Games" },
    { key: "rebus",      label: "Rebus" },
    { key: "sudoku",     label: "Sudoku" },
    { key: "word",       label: "Word Building" },
    { key: "trivia",     label: "Trivia" },
    { key: "caption",    label: "Photo Caption" },
    { key: "pictionary", label: "Office Pictionary" },
  ];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/reports?game=${filter}&limit_recent=30`);
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const rows = (data?.per_user || []).filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (r.name || "").toLowerCase().includes(q) ||
      (r.employee_id || "").toLowerCase().includes(q) ||
      (r.department || "").toLowerCase().includes(q) ||
      (r.email || "").toLowerCase().includes(q)
    );
  });

  const exportCsv = () => {
    if (!data) return;
    const cols = ["Employee ID", "Name", "Email", "Department", "Total Submissions", "Total Correct", "Total Points", "Rebus", "Sudoku", "Word", "Trivia", "Caption", "Pictionary"];
    const lines = [cols.join(",")];
    for (const r of rows) {
      lines.push([
        r.employee_id, JSON.stringify(r.name || ""), r.email || "", JSON.stringify(r.department || ""),
        r.total_submissions, r.total_correct, r.total_points,
        r.by_game.rebus, r.by_game.sudoku, r.by_game.word, r.by_game.trivia, r.by_game.caption, r.by_game.pictionary,
      ].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tekwissen_funzone_report_${filter}_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success("Report exported");
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="space-y-6">
      <TopWinnersCard />
      <PlaytimeReportCard />
      {/* Filter + Stat strip */}
      <div className="brutal-card p-5">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest font-mono text-neutral-500 mb-1 flex items-center gap-1">
              <Filter size={12}/> Filter by game
            </div>
            <div className="flex flex-wrap gap-2" data-testid="report-game-filter">
              {GAMES.map((g) => (
                <button
                  key={g.key}
                  data-testid={`report-filter-${g.key}`}
                  onClick={() => setFilter(g.key)}
                  className={`px-3 py-1 border-2 border-black text-xs font-black uppercase ${filter === g.key ? "bg-black text-white" : "bg-white hover:bg-[#FFD600]"}`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>
          <button
            data-testid="report-export-csv-btn"
            onClick={exportCsv}
            disabled={!data || rows.length === 0}
            className="brutal-btn flex items-center gap-2 text-sm disabled:opacity-50"
          >
            <Download size={14}/> Export CSV
          </button>
        </div>

        {loading ? (
          <div className="text-sm text-neutral-500 py-6 text-center">Loading insights…</div>
        ) : data ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="report-summary">
            <div className="border-2 border-black p-3 bg-[#39FF14]">
              <div className="text-xs uppercase font-mono text-black/70">Total Users</div>
              <div className="text-3xl font-black">{data.summary.total_users}</div>
            </div>
            <div className="border-2 border-black p-3 bg-[#FFD600]">
              <div className="text-xs uppercase font-mono text-black/70">Active Players</div>
              <div className="text-3xl font-black">{data.summary.active_users}</div>
            </div>
            <div className="border-2 border-black p-3 bg-white">
              <div className="text-xs uppercase font-mono text-neutral-500">Total Submissions</div>
              <div className="text-3xl font-black">{data.summary.total_submissions}</div>
            </div>
            <div className="border-2 border-black p-3 bg-white">
              <div className="text-xs uppercase font-mono text-neutral-500">Currently Filtering</div>
              <div className="text-xl font-black uppercase mt-1">{GAMES.find((g) => g.key === filter)?.label || filter}</div>
            </div>
          </div>
        ) : null}

        {/* Per-game strip */}
        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-4">
            {Object.entries(data.summary.by_game).map(([k, v]) => (
              <div key={k} className="border-2 border-black p-3 bg-neutral-50" data-testid={`report-game-stat-${k}`}>
                <div className="text-xs uppercase font-mono text-neutral-500">{v.label}</div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-black">{v.submissions}</span>
                  <span className="text-xs text-neutral-500">subs</span>
                </div>
                <div className="text-xs text-neutral-500">{v.unique_players} players</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Per-user table */}
      <div className="brutal-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="font-black uppercase flex items-center gap-2"><Users size={18}/> Participation by User ({rows.length})</h3>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400"/>
            <input
              data-testid="report-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name / ID / dept…"
              className="brutal-input pl-7 text-sm py-1"
              style={{ width: 240 }}
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="report-user-table">
            <thead><tr className="bg-black text-white text-xs">
              <th className="p-2 text-left">Emp ID</th>
              <th className="p-2 text-left">Name</th>
              <th className="p-2 text-left">Dept</th>
              <th className="p-2 text-right">Total</th>
              <th className="p-2 text-right">Correct</th>
              <th className="p-2 text-right">Pts</th>
              <th className="p-2 text-center">Rebus</th>
              <th className="p-2 text-center">Sudoku</th>
              <th className="p-2 text-center">Word</th>
              <th className="p-2 text-center">Trivia</th>
              <th className="p-2 text-center">Caption</th>
              <th className="p-2 text-center">Pictionary</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_id} className="border-b border-neutral-200" data-testid={`report-user-row-${r.employee_id}`}>
                  <td className="p-2 font-mono text-xs">{r.employee_id}{r.is_demo && <span className="ml-1 text-[10px] bg-[#FFD600] border border-black px-1">DEMO</span>}</td>
                  <td className="p-2 font-bold">{r.name}</td>
                  <td className="p-2 text-xs">{r.department || <span className="text-neutral-400">—</span>}</td>
                  <td className="p-2 text-right font-mono font-black">{r.total_submissions}</td>
                  <td className="p-2 text-right font-mono">{r.total_correct}</td>
                  <td className="p-2 text-right font-mono">{r.total_points}</td>
                  <td className="p-2 text-center font-mono text-xs">{r.by_game.rebus || "—"}</td>
                  <td className="p-2 text-center font-mono text-xs">{r.by_game.sudoku || "—"}</td>
                  <td className="p-2 text-center font-mono text-xs">{r.by_game.word || "—"}</td>
                  <td className="p-2 text-center font-mono text-xs">{r.by_game.trivia || "—"}</td>
                  <td className="p-2 text-center font-mono text-xs">{r.by_game.caption || "—"}</td>
                  <td className="p-2 text-center font-mono text-xs">{r.by_game.pictionary || "—"}</td>
                </tr>
              ))}
              {rows.length === 0 && !loading && (
                <tr><td colSpan={12} className="p-6 text-center text-neutral-500">
                  No participation records {filter !== "all" ? `for ${GAMES.find((g) => g.key === filter)?.label}` : ""} yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent submissions feed */}
      {data && data.recent && data.recent.length > 0 && (
        <div className="brutal-card p-5">
          <h3 className="font-black uppercase mb-3 flex items-center gap-2"><Sparkles size={18}/> Recent Submissions</h3>
          <div className="space-y-1.5 max-h-96 overflow-y-auto" data-testid="report-recent-feed">
            {data.recent.map((s, i) => (
              <div key={i} className="flex items-center justify-between gap-3 border-2 border-neutral-200 px-3 py-2 text-xs">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="border border-black px-1.5 py-0.5 font-black uppercase text-[10px] bg-[#FFD600]">{s.game_label}</span>
                  <span className="font-bold truncate">{s.name || "Unknown"}</span>
                  {s.department && <span className="text-neutral-500 hidden sm:inline">· {s.department}</span>}
                  {s.details && <span className="text-neutral-500 hidden md:inline truncate">{String(s.details).slice(0, 40)}</span>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {s.correct && <CheckCircle2 size={14} className="text-green-600"/>}
                  {s.points > 0 && <span className="font-mono font-black">+{s.points}</span>}
                  <span className="text-neutral-400 font-mono">{fmtDate(s.submitted_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportsPanel;
